import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Button, Stack, Card, CardContent, Chip, Avatar, LinearProgress, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SyncIcon from '@mui/icons-material/Sync';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PaidIcon from '@mui/icons-material/Paid';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { useSnackbar } from 'notistack';
import { getGlobalCookie, clearCookie, checkApiStatus } from '../../utils';
import { PageLoader } from '../../shared_elements';
import { globalPostService } from '../../utils/globalApiServices';
import { pullInvoicePaymentsApi, syncChartOfAccountsApi, syncInvoicesApi, syncProductServiceApi } from './apiService';
import { trackActivity } from '../../utils/mixpanel';
const sync_from_qb_config = [
    { key: 'processed', label: 'Processed', color: '#1565c0', bg: '#e3f2fd', Icon: ReceiptLongIcon },
    { key: 'matched', label: 'Invoices Matched', color: '#6a1b9a', bg: '#f3e5f5', Icon: CompareArrowsIcon },
    { key: 'paid', label: 'Invoice Paid', color: '#2e7d32', bg: '#e8f5e9', Icon: PaidIcon },
    { key: 'partially_paid', label: 'Invoice Partially Paid', color: '#e65100', bg: '#fff3e0', Icon: MoneyOffIcon },
    { key: 'summary_paid', label: 'Summary Invoice Paid', color: '#00695c', bg: '#e0f2f1', Icon: PaidIcon },
    { key: 'summary_partially_paid', label: 'Summary Invoice Partially Paid', color: '#4527a0', bg: '#ede7f6', Icon: MoneyOffIcon },
    { key: 'skipped', label: 'Skipped', color: '#546e7a', bg: '#eceff1', Icon: SkipNextIcon },
];
const ThirdPartyLanding = (props) => {
    const { enqueueSnackbar } = useSnackbar();
    const [remaining, setRemaining] = useState(null);
    const [pageLoader, setPageLoader] = useState(false);
    const [successList, setSuccessList] = useState([]);
    const [errorList, setErrorList] = useState([]);
    const [syncSummary, setSyncSummary] = useState(null);

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: 'QuickBooks Sync', page_type });
    };

    useEffect(() => {
        trackActivity('Page Viewed', { page_title: 'QuickBooks Sync' });
        const syncState = getGlobalCookie('thirdPartyState') || null;
        const syncAccState = getGlobalCookie('thirdPartyAccountState') || null;
        const syncProductState = getGlobalCookie('thirdPartyProductState') || null;
        const syncInvoiceState = getGlobalCookie('thirdPartyInvoiceState') || null;
        const syncInvoicePullState = getGlobalCookie('thirdPartyInvoicePullState') || null;
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const error = params.get('error');
        const realmId = params.get('realmId');
        if (error === 'access_denied') {
            clearCookie('thirdPartyState');
            clearCookie('thirdPartyAccountState');
            clearCookie('thirdPartyProductState');
            clearCookie('thirdPartyInvoiceState');
            clearCookie('thirdPartyInvoicePullState');
            if (window.opener) {
                window.opener.location.reload();
                window.open('', '_self');
                window.close();
            }
            return;
        }
        const synState = syncState === 'SyncFromQB' ? Math.floor(Math.random() * 9000) + 1000 : params.get('state');
        if (code === 'error') return;
        setPageLoader(true);
        const syncUrl = syncAccState === 'SyncAccFromQB' ? syncChartOfAccountsApi() : syncProductState === 'SyncProductFromQB' ? syncProductServiceApi() : syncInvoiceState === 'SyncInvoiceFromQB' ? syncInvoicesApi() : syncInvoicePullState === 'SyncInvoicePullFromQB' ? pullInvoicePaymentsApi() : null;
        const apiCall = syncUrl ? globalPostService(syncUrl, { auth_code: code, realm_id: realmId, state_token: synState }) : null;
        if (!apiCall) { setPageLoader(false); return; }
        apiCall.then((response) => {
            setPageLoader(false);
            const respData = response?.data?.data;
            if (checkApiStatus(response)) {
                enqueueSnackbar(response?.data.message, { variant: 'success' });
                if (respData && typeof respData.processed === 'number') setSyncSummary(respData);
                setErrorList(Array.isArray(respData?.errors) && respData.errors.length > 0 ? respData.errors : []);
                setSuccessList(Array.isArray(respData?.results) && respData.results.length > 0 ? respData.results : []);
            } else {
                enqueueSnackbar(response?.data.message, { variant: 'error' });
                setErrorList(Array.isArray(respData?.errors) && respData.errors.length > 0 ? respData.errors : []);
                setSuccessList([]);
            }
            clearCookie('thirdPartyState');
            clearCookie('thirdPartyAccountState');
            clearCookie('thirdPartyProductState');
            clearCookie('thirdPartyInvoiceState');
            clearCookie('thirdPartyInvoicePullState');
            let i = 30;
            setRemaining(i);
            const interval = setInterval(() => {
                i--;
                setRemaining(i);
                if (i <= 0) clearInterval(interval);
            }, 1000);
            setTimeout(() => {
                if (window.opener) {
                    window.opener.location.reload();
                    window.open('', '_self');
                    window.close();
                }
            }, 30000);
        });
    }, [props]);

    const code = new URLSearchParams(window.location.search).get('code');
    const isError = code === 'error';
    const hasData = successList.length > 0 || errorList.length > 0;

    return (
        <Box sx={{ height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'linear-gradient(135deg, #f0f4ff 0%, #fafafa 100%)', p: { xs: 1.5, md: 2 }, boxSizing: 'border-box', }}>
            {pageLoader && <PageLoader />}

            <Paper elevation={0} sx={{ mb: 1.5, p: { xs: 2, md: 2.5 }, flexShrink: 0, background: isError ? 'linear-gradient(135deg, #ffebee 0%, #fff 100%)' : 'linear-gradient(135deg, #e8f5e9 0%, #f0f7ff 100%)', border: `1.5px solid ${isError ? '#ef9a9a' : '#a5d6a7'}`, borderRadius: 3, textAlign: 'center', }} >
                <Stack alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 52, height: 52, bgcolor: isError ? '#ffcdd2' : '#c8e6c9' }}>
                        {isError
                            ? <ErrorOutlineIcon sx={{ fontSize: 30, color: '#c62828' }} />
                            : <IntegrationInstructionsIcon sx={{ fontSize: 30, color: '#2e7d32' }} />
                        }
                    </Avatar>
                    <Typography variant="h5" fontWeight={700} color={isError ? 'error.dark' : pageLoader ? 'primary.dark' : 'success.dark'}>
                        {isError ? 'Something went wrong on QuickBooks side' : pageLoader ? 'QuickBooks Authentication Pending…' : 'QuickBooks Authentication Successful'}
                    </Typography>
                    {isError && (
                        <Typography variant="body2" color="text.secondary">Please contact the Sparta team for assistance.</Typography>
                    )}
                    {pageLoader && (
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <SyncIcon sx={{ color: '#1976d2', animation: 'spin 1.4s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">Syncing — please do not refresh this page…</Typography>
                        </Stack>
                    )}
                    {remaining !== null && (
                        <Box sx={{ width: '100%', maxWidth: 360 }}>
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={0.5}>
                                <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                    Closing in <strong>{remaining}s</strong>
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={(remaining / 60) * 100}
                                sx={{ height: 6, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: remaining > 20 ? '#43a047' : '#ef5350' } }}
                            />
                        </Box>
                    )}
                </Stack>
            </Paper>

            {syncSummary && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5, flexShrink: 0 }}>
                    {sync_from_qb_config.map(({ key, label, color, bg, Icon }) => (
                        <Card
                            key={key}
                            elevation={0}
                            sx={{ flex: 1, minWidth: 100, borderRadius: 3, border: `1.5px solid ${color}22`, background: bg, transition: 'transform 0.18s ease, box-shadow 0.18s ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 6px 20px ${color}33` }, }}
                        >
                            <CardContent sx={{ textAlign: 'center', py: '10px !important', px: 1.5 }}>
                                <Avatar sx={{ bgcolor: `${color}18`, mx: 'auto', mb: 0.5, width: 36, height: 36 }}>
                                    <Icon sx={{ color, fontSize: 20 }} />
                                </Avatar>
                                <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
                                    {syncSummary[key] ?? 0}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {label}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            {hasData && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, overflow: 'hidden' }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
                            <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                            <Typography variant="subtitle1" fontWeight={700} color="success.dark">Successful Records</Typography>
                            <Chip label={successList.length} size="small" sx={{ bgcolor: '#c8e6c9', color: '#1b5e20', fontWeight: 700, fontSize: 12 }} />
                        </Stack>
                        <Paper elevation={0} sx={{ border: '1.5px solid #a5d6a7', borderRadius: 2 , overflow: 'hidden'}}>
                            <TableContainer sx={{ height: 400, overflow: 'auto' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ background: '#e8f5e9', fontWeight: 700, width: 40, color: '#1b5e20' }}>#</TableCell>
                                            <TableCell sx={{ background: '#e8f5e9', fontWeight: 700, color: '#1b5e20', width: 160 }}>Invoice ID / Slug</TableCell>
                                            <TableCell sx={{ background: '#e8f5e9', fontWeight: 700, color: '#1b5e20' }}>Details</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {successList.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No success records found</TableCell>
                                            </TableRow>
                                        ) : successList.map((item, idx) => (
                                            <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#f9fbe7' }, '&:hover': { bgcolor: '#f1f8e9' } }}>
                                                <TableCell>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#c8e6c9', color: '#1b5e20' }}>{idx + 1}</Avatar>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontFamily: 'monospace' }}>
                                                        {item?.summary_slug || item?.invoice_id || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item?.message || `Invoice ${item?.invoice_number} synced (QB Id: ${item?.qb_invoice_id}).`}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>

                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <Stack direction="row" alignItems="center" spacing={1} mb={0.75}>
                            <ErrorOutlineIcon sx={{ color: '#c62828', fontSize: 20 }} />
                            <Typography variant="subtitle1" fontWeight={700} color="error.dark">Failed Records</Typography>
                            <Chip label={errorList.length} size="small" sx={{ bgcolor: '#ffcdd2', color: '#b71c1c', fontWeight: 700, fontSize: 12 }} />
                        </Stack>
                        <Paper elevation={0} sx={{ border: '1.5px solid #ef9a9a', borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer sx={{ height: 400, overflow: 'auto' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ background: '#ffebee', fontWeight: 700, width: 40, color: '#b71c1c' }}>#</TableCell>
                                            <TableCell sx={{ background: '#ffebee', fontWeight: 700, color: '#b71c1c', width: 160 }}>Invoice ID / Slug</TableCell>
                                            <TableCell sx={{ background: '#ffebee', fontWeight: 700, color: '#b71c1c' }}>Error Details</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {errorList.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>No errors found</TableCell>
                                            </TableRow>
                                        ) : errorList.map((item, idx) => (
                                            <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#fff8f8' }, '&:hover': { bgcolor: '#ffebee' } }}>
                                                <TableCell>
                                                    <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#ffcdd2', color: '#b71c1c' }}>{idx + 1}</Avatar>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontFamily: 'monospace' }}>
                                                        {item?.summary_slug || item?.invoice_id || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="error.dark">{item?.error}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                </Stack>
            )}

            {!pageLoader && <Box sx={{ mt: 1.5, textAlign: 'center', flexShrink: 0 }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => { if (window.opener) { window.opener.location.reload(); window.open('', '_self'); window.close(); } handleItemClicked('Close Window Button') }}
                    sx={{
                        px: 2, py: 0.8, borderRadius: 3, fontWeight: 700, fontSize: 15,
                        background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                        boxShadow: '0 4px 14px rgba(25,118,210,0.35)',
                        '&:hover': { background: 'linear-gradient(135deg, #1565c0, #1e88e5)', boxShadow: '0 6px 18px rgba(25,118,210,0.45)' },
                    }}
                >
                    Close Window
                </Button>
            </Box>}
        </Box>
    );
};

export default ThirdPartyLanding;
